import { useState } from "react";
import { Loader2, Mail, Send, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const inputCls = "bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs focus:border-[#f7c935] outline-none";

export function WeeklyDigestPanel({ api, societeId, settings, onChanged }) {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState(settings.weekly_digest_recipients || []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);

  const save = async (patch) => {
    setBusy("save");
    try {
      await api.put("/accounting/auto/settings", { societe_id: societeId, ...patch });
      toast.success("Résumé hebdo mis à jour");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };
  const addRecipient = async () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return toast.error("Email invalide");
    const next = [...new Set([...recipients, v])];
    setRecipients(next); setDraft("");
    await save({ weekly_digest_recipients: next });
  };
  const removeRecipient = async (r) => {
    const next = recipients.filter((x) => x !== r);
    setRecipients(next);
    await save({ weekly_digest_recipients: next });
  };
  const loadPreview = async () => {
    setBusy("preview");
    try {
      const r = await api.get("/accounting/auto/digest/preview", { params: { societe_id: societeId } });
      setPreview(r.data);
      if (!recipients.length && r.data.default_recipients?.length) setRecipients([]);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };
  const sendNow = async () => {
    setBusy("send");
    try {
      const r = await api.post("/accounting/auto/digest/send", null, { params: { societe_id: societeId } });
      if (r.data.sent) toast.success(`Résumé envoyé à ${r.data.sent} destinataire(s)`);
      else toast.error(`Envoi impossible : ${(r.data.failed || []).join(", ") || "SMTP non configuré"}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };

  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4" data-testid="weekly-digest-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400" data-testid="digest-toggle">
        <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#38bdf8]" /> Résumé hebdo compta — lundi 08:00
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black ${settings.weekly_digest_enabled ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-slate-500"}`} data-testid="digest-enabled-badge">{settings.weekly_digest_enabled ? "ACTIF" : "DÉSACTIVÉ"}</span>
        </span>
        <span className="text-slate-600">{open ? "masquer" : "afficher"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => save({ weekly_digest_enabled: !settings.weekly_digest_enabled })} className="flex items-center gap-2 text-xs text-slate-300" data-testid="digest-enabled-toggle">
              <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.weekly_digest_enabled ? "bg-[#22c55e]" : "bg-white/15"}`}>
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${settings.weekly_digest_enabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
              Envoi automatique chaque lundi matin
            </button>
            {settings.weekly_digest_last_sent && <span className="text-[11px] text-slate-500">Dernier envoi : {new Date(settings.weekly_digest_last_sent).toLocaleString("fr-FR")}</span>}
            <div className="flex-1" />
            <button onClick={loadPreview} disabled={!!busy} className="text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 flex items-center gap-1.5" data-testid="digest-preview-btn">{busy === "preview" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Aperçu</button>
            <button onClick={sendNow} disabled={!!busy} className="bg-[#38bdf8] hover:bg-[#0ea5e9] text-[#0b0811] font-bold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="digest-send-btn">{busy === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Envoyer maintenant</button>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 mb-1">Destinataires {recipients.length === 0 && <span className="text-amber-300">(vide → tous les admins du groupe)</span>}</div>
            <div className="flex flex-wrap gap-1.5 items-center">
              {recipients.map((r) => (
                <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-200 flex items-center gap-1" data-testid={`digest-recipient-${r}`}>
                  {r} <button onClick={() => removeRecipient(r)} className="text-slate-500 hover:text-red-400" data-testid={`digest-recipient-remove-${r}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRecipient()} onBlur={addRecipient} placeholder="ajouter un email + Entrée" className={`${inputCls} w-56`} data-testid="digest-recipient-input" />
              {busy === "save" && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />}
            </div>
          </div>
        </div>
      )}
      {preview && (
        <Dialog open onOpenChange={() => setPreview(null)}>
          <DialogContent className="bg-white text-[#14101c] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="digest-preview-dialog">
            <DialogHeader><DialogTitle className="text-sm">Aperçu — {preview.data.societe_name} · destinataires : {preview.recipients.join(", ")}</DialogTitle></DialogHeader>
            <div dangerouslySetInnerHTML={{ __html: preview.html }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
